import { Button, FieldError, Input, TextField } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

type AccountCreateProps = {
  question?: string;
  description?: string;
  /** Small uppercase label rendered above the question in accent color. */
  eyebrow?: string;
  /** Optional blurred portrait image displayed above the form. */
  portraitUrl?: string;
  /** Pre-filled email from a prior EmailStep. */
  prefilledEmail?: string;
  /** Email input placeholder text. Defaults to "Email". */
  emailPlaceholder?: string;
  /** CTA button label. Defaults to "Create free account →". */
  ctaLabel?: string;
  onSubmitEmail?: (email: string) => void | Promise<void>;
  onContinueWithGoogle?: () => void;
  onContinueWithApple?: () => void;
  /** URL to terms of service page. */
  termsUrl?: string;
  /** URL to privacy policy page. */
  privacyUrl?: string;
  isSubmitting?: boolean;
  errorMessage?: string;
  /**
   * Skip email validation and switch to a generic text input.
   * Used for OTP code entry where the same form layout is reused.
   */
  skipValidation?: boolean;
  /** When `"before_email"`, SSO buttons render above the email input. */
  ssoPosition?: "before_email" | "after_email";
  /** Custom text for the separator between SSO and email sections. */
  separatorLabel?: string;
  /** Render the CTA button inline with the email input. */
  inlineCta?: boolean;
  /** Custom disclaimer text shown below the form (replaces legal footer). */
  disclaimer?: string;
  className?: string;
  testId?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Separator = ({ label = "or" }: { label?: string }) => (
  <div className="relative my-2 flex items-center">
    <div className="h-px flex-1" style={{ background: "hsl(var(--brand-bg-foreground) / 0.10)" }} />
    <span
      className="px-3 text-xs font-medium uppercase tracking-wider"
      style={{ color: "hsl(var(--hero-fg-muted))" }}
    >
      {label}
    </span>
    <div className="h-px flex-1" style={{ background: "hsl(var(--brand-bg-foreground) / 0.10)" }} />
  </div>
);

type SsoButtonsProps = {
  onContinueWithGoogle?: () => void;
  onContinueWithApple?: () => void;
};

const SsoButtons = ({ onContinueWithGoogle, onContinueWithApple }: SsoButtonsProps) => (
  <>
    {onContinueWithGoogle && (
      <Button
        variant="outline"
        size="lg"
        fullWidth
        onPress={onContinueWithGoogle}
        className="rounded-full"
      >
        <Icon icon="logos:google-icon" width={18} height={18} className="mr-2" />
        Continue with Google
      </Button>
    )}
    {onContinueWithApple && (
      <Button
        variant="outline"
        size="lg"
        fullWidth
        onPress={onContinueWithApple}
        className="rounded-full"
      >
        <Icon icon="ic:baseline-apple" width={18} height={18} className="mr-2" />
        Continue with Apple
      </Button>
    )}
  </>
);

const Portrait = ({ url }: { url: string }) => (
  <div className="flex justify-center">
    <div
      className="h-[132px] w-[100px] overflow-hidden rounded-2xl"
      style={{
        boxShadow: "0 0 32px hsl(var(--brand-primary) / 0.25)",
      }}
    >
      <ResponsiveAsset
        src={url}
        alt=""
        loading="eager"
        widthHint={100}
        className="h-full w-full object-cover"
        style={{ filter: "blur(2px)" }}
      />
    </div>
  </div>
);

const LegalFooter = ({ termsUrl, privacyUrl }: { termsUrl?: string; privacyUrl?: string }) => {
  if (!termsUrl && !privacyUrl) return null;
  return (
    <p
      className="mt-1 text-center text-xs leading-relaxed"
      style={{ color: "hsl(var(--hero-fg-muted))" }}
    >
      By continuing you confirm you are 18+ and agree to our{" "}
      {termsUrl && (
        <a
          href={termsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline"
          style={{ color: "hsl(var(--brand-primary))" }}
        >
          Terms
        </a>
      )}
      {termsUrl && privacyUrl && " and "}
      {privacyUrl && (
        <a
          href={privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium underline"
          style={{ color: "hsl(var(--brand-primary))" }}
        >
          Privacy Policy
        </a>
      )}
      .
    </p>
  );
};

const Disclaimer = ({ text }: { text: string }) => (
  <p
    className="mt-1 flex items-start gap-2 text-xs leading-relaxed"
    style={{ color: "hsl(var(--hero-fg-muted))" }}
  >
    <span
      className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full"
      style={{ background: "hsl(var(--brand-primary))" }}
    />
    {text}
  </p>
);

const AccountCreateImpl = ({
  question = "Create your account",
  description = "We'll send a 6-digit code to verify your email.",
  eyebrow,
  portraitUrl,
  prefilledEmail = "",
  emailPlaceholder = "Email",
  ctaLabel = "Create free account \u2192",
  skipValidation = false,
  onSubmitEmail,
  onContinueWithGoogle,
  onContinueWithApple,
  termsUrl,
  privacyUrl,
  isSubmitting,
  errorMessage,
  ssoPosition = "after_email",
  separatorLabel,
  inlineCta = false,
  disclaimer,
  className,
  testId,
}: AccountCreateProps) => {
  const [emailInput, setEmailInput] = useState(prefilledEmail);

  const inputValid = skipValidation
    ? emailInput.trim().length > 0
    : EMAIL_REGEX.test(emailInput.trim());

  const hasSso = !!(onContinueWithGoogle || onContinueWithApple);

  const handleSubmitEmail = (): void => {
    if (!inputValid || !onSubmitEmail) return;
    void onSubmitEmail(emailInput.trim());
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading
        title={question}
        description={description}
        eyebrow={eyebrow}
        editablePath="question"
      />
      {portraitUrl && <Portrait url={portraitUrl} />}

      {ssoPosition === "before_email" && hasSso && (
        <>
          <SsoButtons
            onContinueWithGoogle={onContinueWithGoogle}
            onContinueWithApple={onContinueWithApple}
          />
          <Separator label={separatorLabel ?? "email"} />
        </>
      )}

      {inlineCta ? (
        <>
          <TextField value={emailInput} onChange={setEmailInput} isInvalid={!!errorMessage}>
            <div className="flex items-center gap-2">
              <Input
                type={skipValidation ? "text" : "email"}
                inputMode={skipValidation ? "numeric" : "email"}
                autoComplete={skipValidation ? "one-time-code" : "email"}
                placeholder={emailPlaceholder}
                className="flex-1"
              />
              <Button
                variant="primary"
                size="md"
                onPress={handleSubmitEmail}
                isDisabled={!inputValid || isSubmitting}
                className="flex-shrink-0 rounded-full"
              >
                {ctaLabel}
              </Button>
            </div>
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
          </TextField>
        </>
      ) : (
        <>
          <TextField value={emailInput} onChange={setEmailInput} isInvalid={!!errorMessage}>
            <Input
              type={skipValidation ? "text" : "email"}
              inputMode={skipValidation ? "numeric" : "email"}
              autoComplete={skipValidation ? "one-time-code" : "email"}
              placeholder={emailPlaceholder}
            />
            {errorMessage && <FieldError>{errorMessage}</FieldError>}
          </TextField>
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onPress={handleSubmitEmail}
            isDisabled={!inputValid || isSubmitting}
            className="rounded-full"
          >
            {ctaLabel}
          </Button>
        </>
      )}

      {ssoPosition === "after_email" && hasSso && (
        <>
          <Separator label={separatorLabel ?? "or"} />
          <SsoButtons
            onContinueWithGoogle={onContinueWithGoogle}
            onContinueWithApple={onContinueWithApple}
          />
        </>
      )}

      {disclaimer ? (
        <Disclaimer text={disclaimer} />
      ) : (
        <LegalFooter termsUrl={termsUrl} privacyUrl={privacyUrl} />
      )}
    </section>
  );
};

export const AccountCreate = memo(AccountCreateImpl);

export default AccountCreate;
